/**
 * App Component
 *
 * Root application component that sets up:
 * - MUI Theme Provider with CssBaseline for consistent styling
 * - React Router for navigation
 * - Error Boundary for crash protection
 * - Auth store initialization on mount
 *
 * @module App
 */

import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { ErrorBoundary, AppRouter } from './routes';
import { useAuthStore } from './stores/useAuthStore';

// =============================================================================
// Theme
// =============================================================================

/**
 * Default MUI theme.
 * No customization for MVP - uses MUI defaults.
 */
const theme = createTheme();

// =============================================================================
// Boot Sequence Component
// =============================================================================

/**
 * AppBootstrap - Initializes auth store on mount.
 *
 * This component wraps the router and triggers the boot sequence.
 * The initialize action is idempotent, so calling it multiple times is safe.
 */
function AppBootstrap({ children }: { children: React.ReactNode }) {
  const initialize = useAuthStore((state) => state.initialize);

  useEffect(() => {
    // Trigger boot sequence
    initialize();
  }, [initialize]);

  return <>{children}</>;
}

// =============================================================================
// App Component
// =============================================================================

/**
 * App - Root component with all providers and routing.
 *
 * Provider hierarchy:
 * 1. ThemeProvider - MUI theming
 * 2. CssBaseline - Consistent baseline styles
 * 3. ErrorBoundary - Catch unhandled errors
 * 4. BrowserRouter - React Router context
 * 5. AppBootstrap - Auth initialization
 * 6. AppRouter - Route configuration
 */
export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <BrowserRouter>
          <AppBootstrap>
            <AppRouter />
          </AppBootstrap>
        </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
  );
}
